import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const UserMumbleAssignment = () => {
  const [mumbleUsers, setMumbleUsers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, channelsRes, groupsRes] = await Promise.all([
        api.get('/mumble-admin/mumble-users'),
        api.get('/mumble-admin/channels'),
        api.get('/mumble-admin/groups')
      ]);

      setMumbleUsers(usersRes.data.users);
      setChannels(channelsRes.data.flat);
      setGroups(groupsRes.data.groups);
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);

    // Fetch user's current group memberships
    try {
      const userGroupMemberships = [];
      for (const group of groups) {
        const res = await api.get(`/mumble-admin/groups/${group.group_id}/members`);
        const isMember = res.data.members.some(m => m.user_id === user.user_id);
        if (isMember) {
          userGroupMemberships.push(group.group_id);
        }
      }
      setUserGroups(userGroupMemberships);
    } catch (error) {
      console.error('Error fetching user groups:', error);
    }
  };

  const handleSetDefaultChannel = async (userId, channelId) => {
    try {
      await api.put(`/mumble-admin/mumble-users/${userId}/default-channel`, { channelId });
      toast.success('Default spawn location updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update default channel');
    }
  };

  const handleToggleGroup = async (groupId, isMember) => {
    if (!selectedUser) return;

    try {
      if (isMember) {
        // Remove from group
        await api.delete(`/mumble-admin/groups/${groupId}/members/${selectedUser.user_id}`);
        toast.success('Removed from group');
        setUserGroups(userGroups.filter(g => g !== groupId));
      } else {
        // Add to group
        await api.post(`/mumble-admin/groups/${groupId}/members`, { userId: selectedUser.user_id });
        toast.success('Added to group');
        setUserGroups([...userGroups, groupId]);
      }
    } catch (error) {
      toast.error(`Failed to ${isMember ? 'remove from' : 'add to'} group`);
    }
  };

  const getChannelName = (channelId) => {
    const channel = channels.find(c => c.channel_id === channelId);
    return channel ? channel.name : 'Unknown';
  };

  const getGroupsByChannel = (channelId) => {
    return groups.filter(g => g.channel_id === channelId);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="user-mumble-assignment">
      <h2>User Group & Channel Assignment</h2>
      <p className="help-text">
        Assign users to specific groups and set where they spawn when connecting to Mumble.
      </p>

      <div className="assignment-layout">
        {/* Users List */}
        <div className="users-panel">
          <h3>Mumble Users</h3>
          <div className="user-list">
            {mumbleUsers.map((user) => (
              <div
                key={user.user_id}
                className={`user-item ${selectedUser?.user_id === user.user_id ? 'selected' : ''}`}
                onClick={() => handleSelectUser(user)}
              >
                <div>
                  <strong>{user.name}</strong>
                  {user.email && <div className="user-email">{user.email}</div>}
                  <div className="user-spawn">
                    Spawns in: <span className="channel-badge">{getChannelName(user.lastchannel || 0)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Assignment Panel */}
        <div className="assignment-panel">
          {selectedUser ? (
            <>
              <h3>Assign: {selectedUser.name}</h3>

              {/* Default Spawn Location */}
              <div className="section">
                <h4>Default Spawn Location</h4>
                <p className="help-text">
                  Select which channel this user spawns in when they connect.
                </p>
                <select
                  className="form-control"
                  value={selectedUser.lastchannel || 0}
                  onChange={(e) => handleSetDefaultChannel(selectedUser.user_id, parseInt(e.target.value))}
                >
                  {channels.map((channel) => (
                    <option key={channel.channel_id} value={channel.channel_id}>
                      {channel.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Group Memberships */}
              <div className="section">
                <h4>Group Memberships</h4>
                <p className="help-text">
                  Select which groups this user belongs to. Groups control permissions and access.
                </p>

                {channels.map((channel) => {
                  const channelGroups = getGroupsByChannel(channel.channel_id);
                  if (channelGroups.length === 0) return null;

                  return (
                    <div key={channel.channel_id} className="channel-groups">
                      <div className="channel-header">
                        <strong>📁 {channel.name}</strong>
                      </div>
                      <div className="groups-list">
                        {channelGroups.map((group) => {
                          const isMember = userGroups.includes(group.group_id);
                          return (
                            <div key={group.group_id} className="group-item">
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={isMember}
                                  onChange={() => handleToggleGroup(group.group_id, isMember)}
                                />
                                <span>{group.name}</span>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {groups.length === 0 && (
                  <div className="no-groups">
                    <p>No groups available. Create groups in the Mumble Management tab first.</p>
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="section">
                <h4>Quick Assign</h4>
                <p className="help-text">
                  Set spawn location and add to group in one action.
                </p>
                {channels.map((channel) => {
                  const channelGroups = getGroupsByChannel(channel.channel_id);
                  if (channelGroups.length === 0) return null;

                  return (
                    <div key={channel.channel_id} className="quick-assign-option">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={async () => {
                          await handleSetDefaultChannel(selectedUser.user_id, channel.channel_id);
                          // Add to first group in channel
                          if (channelGroups.length > 0 && !userGroups.includes(channelGroups[0].group_id)) {
                            await handleToggleGroup(channelGroups[0].group_id, false);
                          }
                        }}
                      >
                        Assign to {channel.name}
                        {channelGroups.length > 0 && ` (${channelGroups[0].name})`}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="no-selection">
              <p>← Select a user from the list to manage their assignments</p>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .user-mumble-assignment {
          padding: 20px;
        }
        .help-text {
          color: #6c757d;
          margin: 10px 0;
          font-size: 0.9em;
        }
        .assignment-layout {
          display: grid;
          grid-template-columns: 350px 1fr;
          gap: 20px;
          margin-top: 20px;
        }
        @media (max-width: 768px) {
          .assignment-layout {
            grid-template-columns: 1fr;
          }
        }
        .users-panel,
        .assignment-panel {
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 15px;
          background: white;
        }
        .user-list {
          max-height: 600px;
          overflow-y: auto;
        }
        .user-item {
          padding: 12px;
          margin: 8px 0;
          border: 1px solid #ddd;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .user-item:hover {
          background-color: #f8f9fa;
          border-color: #667eea;
        }
        .user-item.selected {
          background-color: #e7f3ff;
          border-color: #667eea;
          border-width: 2px;
        }
        .user-email {
          font-size: 0.85em;
          color: #6c757d;
          margin-top: 2px;
        }
        .user-spawn {
          font-size: 0.85em;
          margin-top: 5px;
          color: #495057;
        }
        .channel-badge {
          background-color: #667eea;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .section {
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 1px solid #eee;
        }
        .section:last-child {
          border-bottom: none;
        }
        .section h4 {
          margin-bottom: 10px;
          color: #333;
        }
        .form-control {
          width: 100%;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 1em;
        }
        .channel-groups {
          margin-bottom: 20px;
        }
        .channel-header {
          padding: 8px;
          background-color: #f8f9fa;
          border-radius: 4px;
          margin-bottom: 10px;
        }
        .groups-list {
          padding-left: 20px;
        }
        .group-item {
          padding: 8px 0;
        }
        .checkbox-label {
          display: flex;
          align-items: center;
          cursor: pointer;
          font-size: 0.95em;
        }
        .checkbox-label input[type="checkbox"] {
          margin-right: 10px;
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
        .checkbox-label span {
          user-select: none;
        }
        .no-groups,
        .no-selection {
          text-align: center;
          padding: 40px;
          color: #6c757d;
        }
        .quick-assign-option {
          margin: 8px 0;
        }
        .btn {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
        }
        .btn-secondary {
          background-color: #6c757d;
          color: white;
        }
        .btn-sm {
          padding: 6px 12px;
          font-size: 0.9em;
        }
      `}</style>
    </div>
  );
};

export default UserMumbleAssignment;
